import AddIcon from "@material-ui/icons/Add";
import HomeIcon from "@material-ui/icons/Home";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import ReceiptIcon from "@material-ui/icons/Receipt";
import clsx from "clsx";
import React, { FC, useState } from "react";
import { Auth } from "aws-amplify";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import ConfirmationModal from "../ConfirmationModal";
import classes from "./Sidebar.module.scss";
import logo from "../../../assets/images/logo_blue_white.svg";
import { CAMPAIGN_STEPS } from "../../../types/campaignTypes";
import { ROUTE_PATHS } from "../../../types/RoutePaths";
import { steps as campaignSteps } from "../../../pages/ManagedCampaignCreatePage/steps";
import { resetAppState } from "../../../redux/reducers/reset";
import { resetCampaignToCreateState, setCampaignSteps } from "../../../redux/reducers/campaignToCreate";

const useStyles = makeStyles({
  list: {
    width: 300,
  },
  fullList: {
    width: "auto",
  },
  sidebarLogo: {
    height: "50px",
    marginTop: 23,
    marginLeft: 40,
    marginBottom: 37,
    cursor: "pointer",
  },
  listItem: {
    padding: "0 40px",
    height: 48,
    textDecoration: "none",
    cursor: "pointer",
  },
  listItemIcon: {
    color: "#fff",
    minWidth: 24,
    marginRight: 12,
  },
  listItemText: {
    lineHeight: 19,
    fontWeight: 500,
    color: "#fff",
  },
});

type SidebarListProps = {
  handleClose: () => void;
};

const SidebarList: FC<SidebarListProps> = ({ handleClose }) => {
  const [isLogOutConfirmationOpen, setIsLogOutConfirmationOpen] = useState<boolean>(false);
  const handleOpenLogOutConfirmation = () => {
    setIsLogOutConfirmationOpen(true);
  };

  const handleCloseLogOutConfirmation = () => {
    setIsLogOutConfirmationOpen(false);
    handleClose();
  };

  const MUIclasses = useStyles();

  const history = useHistory();
  const location = useLocation();

  const dispatch = useDispatch();

  const handleNavigation = (route: string, steps?: CAMPAIGN_STEPS[]) => () => {
    dispatch(resetCampaignToCreateState());
    dispatch(setCampaignSteps(null));
    if (steps && steps.length > 0) {
      dispatch(setCampaignSteps(steps));
    }
    history.push(route, { from: location.pathname });
    handleClose();
  };

  const handleLogOut = async () => {
    await Auth.signOut();
    localStorage.clear();
    sessionStorage.clear();
    dispatch(resetAppState());
    history.push("/");
  };

  return (
    <div className={clsx(MUIclasses.list)} role="presentation">
      <img className={MUIclasses.sidebarLogo} id="sidebar-logo" src={logo} alt="sidebar logo" />
      <List>
        <ListItem onClick={handleNavigation(ROUTE_PATHS.DASHBOARD)} className={MUIclasses.listItem}>
          <ListItemIcon className={MUIclasses.listItemIcon}>
            <HomeIcon />
          </ListItemIcon>
          <ListItemText className={MUIclasses.listItemText} primary="Home" />
        </ListItem>

        <ListItem onClick={handleNavigation(ROUTE_PATHS.ADVERTISERS_LIST)} className={MUIclasses.listItem}>
          <ListItemIcon className={MUIclasses.listItemIcon}>
            <PersonAddIcon />
          </ListItemIcon>
          <ListItemText className={MUIclasses.listItemText} primary="Advertisers" />
        </ListItem>

        <ListItem onClick={handleNavigation(ROUTE_PATHS.INVOICES)} className={MUIclasses.listItem}>
          <ListItemIcon className={MUIclasses.listItemIcon}>
            <ReceiptIcon />
          </ListItemIcon>
          <ListItemText className={MUIclasses.listItemText} primary="Invoices" />
        </ListItem>

        <ListItem
          onClick={handleNavigation(
            ROUTE_PATHS.MANAGED_CAMPAIGN_CREATE,
            campaignSteps.map((step) => step.name),
          )}
          className={MUIclasses.listItem}
        >
          <ListItemIcon className={MUIclasses.listItemIcon}>
            <AddIcon />
          </ListItemIcon>
          <ListItemText className={MUIclasses.listItemText} primary="ABM Campaign" />
        </ListItem>

        <ListItem onClick={handleNavigation(ROUTE_PATHS.CUSTOM_CAMPAIGN_CREATE)} className={MUIclasses.listItem}>
          <ListItemIcon className={MUIclasses.listItemIcon}>
            <AddIcon />
          </ListItemIcon>
          <ListItemText className={MUIclasses.listItemText} primary="Custom Campaign" />
        </ListItem>

        <ListItem onClick={handleNavigation(ROUTE_PATHS.CAMPAIGN_CREATE_FROM_TEMPLATE)} className={MUIclasses.listItem}>
          <ListItemIcon className={MUIclasses.listItemIcon}>
            <AddIcon />
          </ListItemIcon>
          <ListItemText className={MUIclasses.listItemText} primary="Create Campaign From Template" />
        </ListItem>

        <ListItem onClick={handleNavigation(ROUTE_PATHS.CREATE_CAMPAIGN_TEMPLATE)} className={MUIclasses.listItem}>
          <ListItemIcon className={MUIclasses.listItemIcon}>
            <AddIcon />
          </ListItemIcon>
          <ListItemText className={MUIclasses.listItemText} primary="Create Campaign Template" />
        </ListItem>

        <ListItem onClick={handleNavigation(ROUTE_PATHS.CAMPAIGN_TEMPLATES)} className={MUIclasses.listItem}>
          <ListItemIcon className={MUIclasses.listItemIcon}>
            <AddIcon />
          </ListItemIcon>
          <ListItemText className={MUIclasses.listItemText} primary="Edit Campaign Templates" />
        </ListItem>

        <ListItem
          onClick={handleNavigation(ROUTE_PATHS.VISITORS_RE_TARGETING_SEGMENTS)}
          className={MUIclasses.listItem}
        >
          <ListItemIcon className={MUIclasses.listItemIcon}>
            <AddIcon />
          </ListItemIcon>
          <ListItemText className={MUIclasses.listItemText} primary="Visitors Re-targeting Segments" />
        </ListItem>

        <ListItem onClick={handleNavigation(ROUTE_PATHS.CRM_RE_TARGETING_SEGMENTS)} className={MUIclasses.listItem}>
          <ListItemIcon className={MUIclasses.listItemIcon}>
            <AddIcon />
          </ListItemIcon>
          <ListItemText className={MUIclasses.listItemText} primary="CRM Re-targeting Segments" />
        </ListItem>

        <ListItem onClick={handleNavigation(ROUTE_PATHS.PUBLICATION_LISTS)} className={MUIclasses.listItem}>
          <ListItemIcon className={MUIclasses.listItemIcon}>
            <AddIcon />
          </ListItemIcon>
          <ListItemText className={MUIclasses.listItemText} primary="Publication Lists" />
        </ListItem>

        {/* <ListItem onClick={handleNavigation(ROUTE_PATHS.ADS_UPLOAD)} className={MUIclasses.listItem}>
          <ListItemIcon className={MUIclasses.listItemIcon}>
            <AddIcon />
          </ListItemIcon>
          <ListItemText className={MUIclasses.listItemText} primary="Ads Upload" />
        </ListItem> */}

        <ListItem onClick={handleNavigation(ROUTE_PATHS.TARGET_COMPANIES)} className={MUIclasses.listItem}>
          <ListItemIcon className={MUIclasses.listItemIcon}>
            <AddIcon />
          </ListItemIcon>
          <ListItemText className={MUIclasses.listItemText} primary="Target Companies" />
        </ListItem>

        <ListItem onClick={handleNavigation(ROUTE_PATHS.ROLES_AND_TITLES)} className={MUIclasses.listItem}>
          <ListItemIcon className={MUIclasses.listItemIcon}>
            <AddIcon />
          </ListItemIcon>
          <ListItemText className={MUIclasses.listItemText} primary="Roles and Titles" />
        </ListItem>

        <ListItem onClick={handleNavigation(ROUTE_PATHS.ZIP_CODES)} className={MUIclasses.listItem}>
          <ListItemIcon className={MUIclasses.listItemIcon}>
            <AddIcon />
          </ListItemIcon>
          <ListItemText className={MUIclasses.listItemText} primary="Zip Codes" />
        </ListItem>

        <a
          href="https://www.brandgen.io/_hcms/mem/login?redirect_url=https://www.brandgen.io/support-view"
          target="_blank"
          rel="noopener noreferrer"
          className={classes.listItemLink}
        >
          <ListItem className={MUIclasses.listItem}>
            <ListItemText className={MUIclasses.listItemText} primary="Support Portal" />
          </ListItem>
        </a>

        <a
          href="https://app.brandgen.io/terms-of-use"
          target="_blank"
          rel="noopener noreferrer"
          className={classes.listItemLink}
        >
          <ListItem className={MUIclasses.listItem}>
            <ListItemText className={MUIclasses.listItemText} primary="Terms of Use" />
          </ListItem>
        </a>

        <a
          href="https://app.brandgen.io/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          className={classes.listItemLink}
        >
          <ListItem className={MUIclasses.listItem}>
            <ListItemText className={MUIclasses.listItemText} primary="Privacy Policy" />
          </ListItem>
        </a>

        <ListItem onClick={handleOpenLogOutConfirmation} className={MUIclasses.listItem}>
          <ListItemText className={MUIclasses.listItemText} primary="Log out" />
        </ListItem>
      </List>

      <ConfirmationModal
        isOpen={isLogOutConfirmationOpen}
        confirmQuestion="Log out from BrandGen?"
        confirmButtonLabel="LOG OUT"
        handleClose={handleCloseLogOutConfirmation}
        handleConfirm={handleLogOut}
      />
    </div>
  );
};

export default SidebarList;
